import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  makeStyles,
  MenuItem,
  Chip,
  Divider,
} from '@material-ui/core'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import PersonIcon from '@material-ui/icons/Person'
import BusinessIcon from '@material-ui/icons/Business'
import axios from 'axios'
import useClientSearch from '../hooks/useClientSearch'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  resultsList: {
    maxHeight: 220,
    overflow: 'auto',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    marginTop: 4,
  },
  resultItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    padding: '6px 12px',
  },
  typeChip: {
    height: 18,
    fontSize: 10,
    marginRight: 6,
  },
  selectedInfo: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  jobsiteSelect: {
    marginTop: theme.spacing(1),
  },
  clearBtn: {
    color: theme.palette.error.main,
    cursor: 'pointer',
    fontSize: 12,
    textDecoration: 'underline',
    marginTop: 4,
    display: 'inline-block',
  },
  crmFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginBottom: 2,
  },
}))

// Text field that manages its own local state while typing, only commits on blur
const BlurField = ({ value, onCommit, ...props }) => {
  const [local, setLocal] = useState(value || '')
  useEffect(() => { setLocal(value || '') }, [value])
  return (
    <TextField
      {...props}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value || '')) onCommit(local) }}
    />
  )
}

const TYPE_LABELS = {
  customer: 'Customer',
  aspireProperty: 'Aspire Property',
  jobsite: 'Jobsite',
}

const TYPE_COLORS = {
  customer: 'primary',
  aspireProperty: 'secondary',
  jobsite: 'default',
}

const toIdString = (value) => {
  if (!value) return ''
  if (typeof value === 'object') return String(value._id || value.id || '')
  return String(value)
}

const ClientSiteEditor = ({ proposal, updateCrmSnapshot, updateCrmSnapshots, url, updateJobsite, updateModuleOverrides }) => {
  const classes = useStyles()
  const { results, searching, search, clear } = useClientSearch(url)
  const [query, setQuery] = useState('')
  const [selectedClient, setSelectedClient] = useState(null)
  const [jobsites, setJobsites] = useState([])
  const [loadingJobsites, setLoadingJobsites] = useState(false)
  const [selectedJobsiteId, setSelectedJobsiteId] = useState(toIdString(proposal?.jobsiteId))
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [contacts, setContacts] = useState([])
  const [selectedContactIdx, setSelectedContactIdx] = useState('')
  const [contactOverride, setContactOverride] = useState(false)

  // Load jobsites when a customer is selected
  const fetchJobsites = useCallback(async (sourceId) => {
    if (!sourceId || !url) return
    setLoadingJobsites(true)
    try {
      const res = await axios.get(`${url}/api/mtc-proposals/clients/${sourceId}/jobsites`)
      setJobsites(res.data || [])
    } catch (err) {
      console.error('Failed to fetch jobsites:', err)
      setJobsites([])
    } finally {
      setLoadingJobsites(false)
    }
  }, [url])

  // If proposal already has crmSource, initialize the selected client state
  useEffect(() => {
    if (proposal?.crmSource?.sourceId && proposal.crmSnapshot?.clientName) {
      setSelectedClient({
        type: proposal.crmSource.type,
        sourceId: proposal.crmSource.sourceId,
        clientName: proposal.crmSnapshot.clientName,
      })
      fetchJobsites(proposal.crmSource.sourceId)
    }
  }, [proposal?.crmSource?.sourceId]) // intentionally sparse deps — only re-run when sourceId changes

  useEffect(() => {
    if (proposal?.jobsiteId) {
      setSelectedJobsiteId(toIdString(proposal.jobsiteId))
    }
  }, [proposal?.jobsiteId])

  const applyJobsiteDetailsToState = useCallback((details) => {
    const jobsite = details?.jobsite || null
    const jobsiteId = toIdString(jobsite?._id)
    const customerId = toIdString(details?.customer?._id || jobsite?.client)
    if (jobsiteId) {
      setSelectedJobsiteId(jobsiteId)
      setJobsites((current) => {
        const hasJobsite = current.some(j => toIdString(j._id) === jobsiteId)
        return hasJobsite ? current : [jobsite, ...current]
      })
    }

    if (details?.customer || customerId || jobsite) {
      setSelectedClient((current) => current || {
        type: 'customer',
        sourceId: customerId,
        clientName: details?.customer?.displayName || proposal?.crmSnapshot?.clientName || jobsite?.name || '(unnamed)',
      })
    }

    if (details?.contacts && details.contacts.length > 0) {
      setContacts(details.contacts)
      setSelectedContactIdx('')
      setContactOverride(false)
    } else {
      setContacts([])
    }
  }, [proposal?.crmSnapshot?.clientName])

  const applyJobsiteDetailsToModules = useCallback((details) => {
    if (!updateModuleOverrides || !details) return
    const overrides = {}
    if (details.mapImageUrl) overrides.mapImageUrl = details.mapImageUrl
    if (details.serviceAreaData?.serviceAreas) {
      overrides.serviceAreas = details.serviceAreaData.serviceAreas
      if (details.serviceAreaData.serviceAreas.length > 0) {
        overrides.mapCaption = 'Saved service areas shown in orange and yellow. These represent the maintained areas for this proposal.'
      }
    }
    if (details.parcelData?.propertyLines) overrides.propertyLines = details.parcelData.propertyLines
    if (details.jobsite) {
      const addrParts = [details.jobsite.address, details.jobsite.city, details.jobsite.state, details.jobsite.zip].filter(Boolean)
      if (addrParts.length) overrides.address = addrParts.join(', ')
      if (details.jobsite.APN) overrides.apn = details.jobsite.APN
    }
    if (Object.keys(overrides).length) updateModuleOverrides('serviceArea', overrides)
  }, [updateModuleOverrides])

  useEffect(() => {
    const jobsiteId = toIdString(proposal?.jobsiteId)
    if (!jobsiteId || !url) return undefined
    const alreadyLoaded = selectedClient && jobsites.some(j => toIdString(j._id) === jobsiteId)
    if (alreadyLoaded) return undefined

    let cancelled = false
    setLoadingDetails(true)
    axios.get(`${url}/api/mtc-proposals/jobsites/${jobsiteId}/details`)
      .then((res) => {
        if (cancelled) return
        applyJobsiteDetailsToState(res.data)
        applyJobsiteDetailsToModules(res.data)
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to hydrate proposal jobsite:', err)
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false)
      })
    return () => {
      cancelled = true
    }
  }, [proposal?.jobsiteId, url, selectedClient, jobsites, applyJobsiteDetailsToState, applyJobsiteDetailsToModules])

  const handleSearch = (e) => {
    const val = e.target.value
    setQuery(val)
    search(val)
  }

  const handleSelectResult = (result) => {
    setSelectedClient(result)
    setQuery('')
    clear()

    // Bulk-fill CRM fields + clear contact fields until they pick one
    const crmUpdate = {
      contactName: '',
      contactTitle: '',
      contactEmail: '',
      contactPhone: '',
    }
    if (result.clientName) crmUpdate.clientName = result.clientName
    if (result.clientAddress) crmUpdate.clientAddress = result.clientAddress
    updateCrmSnapshots(crmUpdate)

    // If it's a jobsite result, auto-select that jobsite
    if (result.type === 'jobsite' && result.jobsiteId) {
      setSelectedJobsiteId(result.jobsiteId)
      handleJobsiteSelect(result.jobsiteId)
    }

    // Fetch jobsites for this customer
    if (result.sourceId) {
      fetchJobsites(result.sourceId)
    }
  }

  const handleJobsiteSelect = async (jobsiteId) => {
    if (!jobsiteId || !url) return
    const normalizedJobsiteId = toIdString(jobsiteId)
    setSelectedJobsiteId(normalizedJobsiteId)
    updateJobsite(normalizedJobsiteId)
    setLoadingDetails(true)

    try {
      const res = await axios.get(`${url}/api/mtc-proposals/jobsites/${normalizedJobsiteId}/details`)
      const details = res.data
      applyJobsiteDetailsToState(details)

      // Bulk-fill CRM from jobsite details
      const crmUpdate = {}
      if (details.customer?.displayName) crmUpdate.clientName = details.customer.displayName
      if (details.jobsite) {
        const addrParts = [details.jobsite.address, details.jobsite.city, details.jobsite.state, details.jobsite.zip].filter(Boolean)
        if (addrParts.length) crmUpdate.clientAddress = addrParts.join(', ')
      }
      if (Object.keys(crmUpdate).length) updateCrmSnapshots(crmUpdate)

      // Update serviceArea module with parcel/map data (single bulk call)
      applyJobsiteDetailsToModules(details)
    } catch (err) {
      console.error('Failed to fetch jobsite details:', err)
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleContactSelect = (e) => {
    const idx = e.target.value
    if (idx === '__manual') {
      setContactOverride(true)
      setSelectedContactIdx('__manual')
      return
    }
    setContactOverride(false)
    setSelectedContactIdx(idx)
    if (idx !== '' && contacts[idx]) {
      const c = contacts[idx]
      const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
      updateCrmSnapshots({
        contactName: name,
        contactTitle: c.title || '',
        contactEmail: c.email || '',
        contactPhone: c.phone || '',
      })
    }
  }

  const handleClear = () => {
    setSelectedClient(null)
    setJobsites([])
    setSelectedJobsiteId('')
    setContacts([])
    setSelectedContactIdx('')
    setContactOverride(false)
    setQuery('')
    clear()
    // Clear CRM fields so stale data from old client doesn't linger
    updateCrmSnapshots({
      clientName: '',
      clientAddress: '',
      contactName: '',
      contactTitle: '',
      contactEmail: '',
      contactPhone: '',
    })
  }

  const handleJobsiteDropdownChange = (e) => {
    const jId = toIdString(e.target.value)
    if (jId) {
      handleJobsiteSelect(jId)
    }
  }

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle2" gutterBottom>
        Client &amp; Jobsite
      </Typography>

      {!selectedClient ? (
        <>
          <TextField
            label="Search clients or jobsites..."
            value={query}
            onChange={handleSearch}
            size="small"
            variant="outlined"
            fullWidth
            InputProps={{
              endAdornment: searching ? <CircularProgress size={16} /> : null,
            }}
          />
          {results.length > 0 && (
            <List dense className={classes.resultsList}>
              {results.map((r, i) => (
                <ListItem
                  key={`${r.type}-${r.sourceId}-${i}`}
                  className={classes.resultItem}
                  onClick={() => handleSelectResult(r)}
                >
                  {r.type === 'customer' && <PersonIcon style={{ fontSize: 18, marginRight: 8, color: '#888' }} />}
                  {r.type === 'aspireProperty' && <BusinessIcon style={{ fontSize: 18, marginRight: 8, color: '#888' }} />}
                  {r.type === 'jobsite' && <LocationOnIcon style={{ fontSize: 18, marginRight: 8, color: '#888' }} />}
                  <ListItemText
                    primary={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Chip
                          label={TYPE_LABELS[r.type] || r.type}
                          size="small"
                          color={TYPE_COLORS[r.type] || 'default'}
                          className={classes.typeChip}
                        />
                        {r.clientName || r.jobsiteName || '(unnamed)'}
                      </span>
                    }
                    secondary={r.clientAddress}
                    primaryTypographyProps={{ style: { fontSize: 13 } }}
                    secondaryTypographyProps={{ style: { fontSize: 11 } }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </>
      ) : (
        <Box className={classes.selectedInfo}>
          <Typography variant="caption" style={{ color: '#666', fontWeight: 500 }}>
            Selected Client
          </Typography>
          <Typography variant="body2" style={{ fontWeight: 600, marginTop: 4 }}>
            {selectedClient.clientName || '(unnamed)'}
          </Typography>
          <span className={classes.clearBtn} onClick={handleClear}>
            Change client
          </span>
        </Box>
      )}

      {/* Jobsite dropdown */}
      {selectedClient && (
        <Box>
          {loadingJobsites ? (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading jobsites...</Typography>
            </Box>
          ) : jobsites.length > 0 ? (
            <TextField
              select
              label="Select Jobsite"
              value={selectedJobsiteId}
              onChange={handleJobsiteDropdownChange}
              size="small"
              variant="outlined"
              fullWidth
              className={classes.jobsiteSelect}
            >
              <MenuItem value="">
                <em>— Select a jobsite —</em>
              </MenuItem>
              {jobsites.map(j => (
                <MenuItem key={toIdString(j._id)} value={toIdString(j._id)}>
                  {j.name || j.address || j._id}
                  {j.address ? ` — ${j.address}` : ''}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="caption" style={{ color: '#999' }}>
              No jobsites found for this client.
            </Typography>
          )}

          {loadingDetails && (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <CircularProgress size={14} />
              <Typography variant="caption">Loading jobsite details...</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Contact picker */}
      {selectedClient && contacts.length > 0 && (
        <Box>
          <TextField
            select
            label="Contact"
            value={selectedContactIdx}
            onChange={handleContactSelect}
            size="small"
            variant="outlined"
            fullWidth
          >
            <MenuItem value="">
              <em>— Select a contact —</em>
            </MenuItem>
            {contacts.map((c, i) => {
              const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
              const detail = [c.title, c.email].filter(Boolean).join(' · ')
              return (
                <MenuItem key={i} value={i}>
                  {name || '(unnamed)'}{detail ? ` — ${detail}` : ''}
                </MenuItem>
              )
            })}
            <MenuItem value="__manual">
              <em>Type manually...</em>
            </MenuItem>
          </TextField>
        </Box>
      )}

      <Divider style={{ margin: '4px 0' }} />

      {/* All CRM fields — editable snapshot (saves on blur, not per-keystroke) */}
      <Box className={classes.crmFields}>
        <Typography variant="caption" className={classes.sectionLabel}>
          Proposal Client Info (editable)
        </Typography>
        <BlurField
          label="Client Name"
          value={proposal?.crmSnapshot?.clientName || ''}
          onCommit={(v) => updateCrmSnapshot('clientName', v)}
          size="small"
          variant="outlined"
          fullWidth
        />
        <BlurField
          label="Client Address"
          value={proposal?.crmSnapshot?.clientAddress || ''}
          onCommit={(v) => updateCrmSnapshot('clientAddress', v)}
          size="small"
          variant="outlined"
          fullWidth
        />
        <Box className={classes.fieldRow}>
          <BlurField
            label="Contact Name"
            value={proposal?.crmSnapshot?.contactName || ''}
            onCommit={(v) => updateCrmSnapshot('contactName', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
          <BlurField
            label="Contact Title"
            value={proposal?.crmSnapshot?.contactTitle || ''}
            onCommit={(v) => updateCrmSnapshot('contactTitle', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
        </Box>
        <Box className={classes.fieldRow}>
          <BlurField
            label="Contact Email"
            value={proposal?.crmSnapshot?.contactEmail || ''}
            onCommit={(v) => updateCrmSnapshot('contactEmail', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
          <BlurField
            label="Contact Phone"
            value={proposal?.crmSnapshot?.contactPhone || ''}
            onCommit={(v) => updateCrmSnapshot('contactPhone', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
        </Box>
        <BlurField
          label="Contract Date"
          value={proposal?.crmSnapshot?.contractDate || ''}
          onCommit={(v) => updateCrmSnapshot('contractDate', v)}
          size="small"
          variant="outlined"
          fullWidth
          placeholder="e.g. January 2026"
        />
      </Box>

      <Divider style={{ margin: '4px 0' }} />

      {/* Prepared-by fields — who prepared this proposal */}
      <Box className={classes.crmFields}>
        <Typography variant="caption" className={classes.sectionLabel}>
          Prepared by (editable)
        </Typography>
        <BlurField
          label="Company"
          value={proposal?.crmSnapshot?.yourCompany || ''}
          onCommit={(v) => updateCrmSnapshot('yourCompany', v)}
          size="small"
          variant="outlined"
          fullWidth
        />
        <Box className={classes.fieldRow}>
          <BlurField
            label="Name"
            value={proposal?.crmSnapshot?.yourName || ''}
            onCommit={(v) => updateCrmSnapshot('yourName', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
          <BlurField
            label="Title"
            value={proposal?.crmSnapshot?.yourTitle || ''}
            onCommit={(v) => updateCrmSnapshot('yourTitle', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
        </Box>
        <Box className={classes.fieldRow}>
          <BlurField
            label="Email"
            value={proposal?.crmSnapshot?.yourEmail || ''}
            onCommit={(v) => updateCrmSnapshot('yourEmail', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
          <BlurField
            label="Phone"
            value={proposal?.crmSnapshot?.yourPhone || ''}
            onCommit={(v) => updateCrmSnapshot('yourPhone', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ClientSiteEditor
